import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Snackbar,
  Alert,
  Drawer,
  useMediaQuery,
  useTheme,
  Backdrop,
  IconButton,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { Outlet } from 'react-router-dom';
import { useNotification } from '../contexts/ErrorContext';
import Sidebar from './Sidebar';
import './Sidebar.css';

// Use percentage-based width instead of fixed pixels
const expandedSidebarWidth = '280px';
const collapsedSidebarWidth = '80px';

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowY: 'auto',
}));


const MainLayout: React.FC = () => {
  const [isPinned, setIsPinned] = useState(() => {
    const savedPinnedState = localStorage.getItem('sidebarPinned');
    return savedPinnedState ? JSON.parse(savedPinnedState) : false;
  });
  const [open, setOpen] = useState(isPinned); // Initialize open based on pinned state
  const [mobileOpen, setMobileOpen] = useState(false);
  const { notification, showNotification, hideNotification } = useNotification();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const handleApiError = (event: Event) => {
      const customEvent = event as CustomEvent<string>;
      showNotification(customEvent.detail, 'error');
    };

    const handleApiSuccess = (event: Event) => {
      const customEvent = event as CustomEvent<string>;
      showNotification(customEvent.detail, 'success');
    };

    const handleApiInfo = (event: Event) => {
      const customEvent = event as CustomEvent<string>;
      showNotification(customEvent.detail, 'info');
    };

    const handleApiWarning = (event: Event) => {
      const customEvent = event as CustomEvent<string>;
      showNotification(customEvent.detail, 'warning');
    };

    window.addEventListener('apiError', handleApiError);
    window.addEventListener('apiSuccess', handleApiSuccess);
    window.addEventListener('apiInfo', handleApiInfo);
    window.addEventListener('apiWarning', handleApiWarning);

    return () => {
      window.removeEventListener('apiError', handleApiError);
      window.removeEventListener('apiSuccess', handleApiSuccess);
      window.removeEventListener('apiInfo', handleApiInfo);
      window.removeEventListener('apiWarning', handleApiWarning);
    };
  }, [showNotification]);

  useEffect(() => {
    localStorage.setItem('sidebarPinned', JSON.stringify(isPinned));
    // If sidebar is pinned, ensure it's open
    if (isPinned) {
      setOpen(true);
    }
  }, [isPinned]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      // Only toggle if not pinned
      if (!isPinned) {
        setOpen(!open);
      }
    }
  };

  const handlePinToggle = () => {
    setIsPinned(!isPinned);
    // If pinning, ensure sidebar is open
    if (!isPinned) {
      setOpen(true);
    }
  };

  const handleMobileDrawerClose = () => {
    setMobileOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* Mobile Hamburger Menu */}
      {isMobile && (
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: 1300,
            backgroundColor: 'background.paper',
            boxShadow: 2,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {/* Desktop Sidebar */}
      {!isMobile && (
        <Box component="nav" sx={{ width: open ? expandedSidebarWidth : collapsedSidebarWidth, flexShrink: 0 }}>
          <Sidebar
            open={open}
            isMobile={isMobile}
            handleDrawerToggle={handleDrawerToggle}
            isPinned={isPinned} // Pass isPinned state
            handlePinToggle={handlePinToggle} // Pass pin toggle handler
          />
        </Box>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleMobileDrawerClose}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              '& .MuiDrawer-paper': {
                width: expandedSidebarWidth,
                boxSizing: 'border-box',
                border: 'none',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              },
            }}
          >
            <Sidebar
              open={true}
              isMobile={isMobile}
              handleDrawerToggle={handleDrawerToggle}
              handleMobileDrawerClose={handleMobileDrawerClose}
              isPinned={isPinned} // Pass isPinned state
              handlePinToggle={handlePinToggle} // Pass pin toggle handler
            />
          </Drawer>
          <Backdrop
            open={mobileOpen}
            onClick={handleMobileDrawerClose}
            sx={{
              zIndex: (theme) => theme.zIndex.drawer - 1,
              backgroundColor: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(4px)',
            }}
          />
        </>
      )}

      <Main
        open={!isMobile && open}
        sx={{
          marginLeft: isMobile ? 0 : 0,
          width: isMobile ? '100%' : `calc(100% - ${open ? expandedSidebarWidth : collapsedSidebarWidth})`,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Outlet />
      </Main>
      
      <Snackbar open={!!notification} autoHideDuration={6000} onClose={hideNotification}>
        <Alert onClose={hideNotification} severity={notification?.severity || 'info'} sx={{ width: '100%' }}>
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MainLayout;
